import {
    downloadErrorsEcheqRules,
    echeqRuleCreatePreMiddleware,
    echeqRuleCreatePreViewMiddleware,
    echeqRuleCreateSendMiddleware,
} from "middleware/echeqRule";
import { routerActions } from "react-router-redux";
import { types } from "reducers/echeqRule";
import { actions as formActions } from "reducers/form";
import { actions as notificationActions } from "reducers/notification";
import { call, put, take, takeLatest } from "redux-saga/effects";
import { downloadXls } from "util/download";
import * as i18n from "util/i18n";
import { VALIDATION_ERROR } from "util/responses";

const sagas = [
    takeLatest(types.ECHEQ_RULE_PRE_REQUEST, echeqRuleCreatePreRequest),
    takeLatest(types.ECHEQ_RULE_PREVIEW, echeqRuleCreatePreviewRequest),
    takeLatest(types.ECHEQ_RULE_SEND, echeqRuleCreateSend),
    takeLatest(types.DOWNLOAD_ECHEQ_RULE_ERRORS_REQUEST, downloadErrorsEcheqRulesList),
];

export default sagas;

// Devuelve el primer valor "con contenido". A diferencia de ??, considera vacíos
// los strings "", objetos {} y arrays [] (el eco del preview puede devolverlos
// vacíos y no debe pisar lo que cargó el usuario en el form).
const firstNonEmpty = (primary, fallback) => {
    if (primary === null || primary === undefined || primary === "") {
        return fallback;
    }
    if (Array.isArray(primary)) {
        return primary.length === 0 ? fallback : primary;
    }
    if (typeof primary === "object" && Object.keys(primary).length === 0) {
        return fallback;
    }
    return primary;
};

function* echeqRuleCreatePreRequest() {
    const response = yield call(echeqRuleCreatePreMiddleware);
    if (response.type === "W") {
        const message = response?.data?.data?.message || response?.data?.message;
        yield put({ type: types.ECHEQ_RULE_PRE_FAILURE });
        yield put(routerActions.push("/v2/echeqRules"));
        yield put(notificationActions.showNotification(message, "error", ["echeqRules"]));
    } else {
        const {
            accounts,
            actions,
            conditions,
            daysBefore,
            subConditionsAmount,
            subConditionsIssuer,
            subConditionsReceived,
        } = response.data.data;
        yield put({
            type: types.ECHEQ_RULE_PRE_SUCCESS,
            accounts,
            actions,
            conditions,
            daysBefore,
            subConditionsAmount,
            subConditionsIssuer,
            subConditionsReceived,
        });
    }
}

function* echeqRuleCreatePreviewRequest({ valuesForm }) {
    const response = yield call(echeqRuleCreatePreViewMiddleware, valuesForm);

    if (response.type === "W") {
        if (response.data.data?.fixErrorMessage) {
            yield put(
                notificationActions.showNotification(
                    response.data.data.fixErrorMessage,
                    "error",
                    ["echeqRules"],
                    null,
                    false,
                ),
            );
        } else {
            const message = response?.data?.data?.message || response?.data?.message;
            yield put(notificationActions.showNotification(message, "warning", ["echeqRules"], null, false));
        }
        const {
            account,
            action,
            conditions,
            name,
            numberRule,
            currency,
            includesAdmission,
            beneficiary,
            address,
            reference,
            referenceValue,
        } = valuesForm;

        yield put({
            type: types.ECHEQ_RULE_PREVIEW_FAILURE,
            initialValues: {
                account,
                action,
                conditions,
                name,
                numberRule,
                currency,
                includesAdmission,
                beneficiary,
                address,
                reference,
                referenceValue,
            },
        });
    } else {
        const { account, action, conditions, name, numberRule } = response?.data?.data;
        const { currency, includesAdmission, beneficiary, address, reference, referenceValue } = valuesForm;
        yield put({
            type: types.ECHEQ_RULE_PREVIEW_SUCCESS,
            initialValues: {
                account: firstNonEmpty(valuesForm?.account, account),
                action: firstNonEmpty(valuesForm?.action, action),
                conditions: firstNonEmpty(valuesForm?.conditions, conditions),
                name: firstNonEmpty(valuesForm?.name, name),
                numberRule: firstNonEmpty(valuesForm?.numberRule, numberRule),
                currency,
                includesAdmission,
                beneficiary,
                address,
                reference,
                referenceValue,
            },
        });
        yield put(routerActions.replace("/v2/echeqRules/create/confirmation"));
    }
}

function* echeqRuleCreateSend({ valuesForm }) {
    const response = yield call(echeqRuleCreateSendMiddleware, valuesForm);

    if (response.type === "W" && response.data?.code !== "API516W") {
        if (response.data?.code === VALIDATION_ERROR) {
            if (response.data.data.NO_FIELD) {
                yield put(notificationActions.showNotification(response.data.data.NO_FIELD, "error", ["echeqsRule"]));
            } else if (response.data.data?.fixErrorMessage) {
                yield put(
                    notificationActions.showNotification(
                        response.data.data.fixErrorMessage,
                        "error",
                        ["echeqsRule"],
                        null,
                        false,
                    ),
                );
            } else {
                yield put(
                    notificationActions.showNotification(
                        response.data.data.message,
                        "warning",
                        ["echeqRules"],
                        null,
                        false,
                    ),
                );
            }
        } else if (response.data?.data?.signatureError) {
            yield put(
                notificationActions.showNotification(
                    response.data.data.signatureError,
                    "error",
                    ["echeqRules"],
                    null,
                    false,
                ),
            );
        } else {
            yield put(
                notificationActions.showNotification(response.data?.message, "warning", ["echeqRules"], null, false),
            );
        }
        yield put({ type: types.ECHEQ_RULE_SEND_FAILURE });
    } else {
        if (response?.data?.data?.signatureError) {
            yield put(
                notificationActions.showNotification(
                    response.data.data.signatureError,
                    "error",
                    ["echeqRules"],
                    null,
                    false,
                ),
            );
        }
        yield put(
            formActions.readTransaction({
                pathname: `/transaction/${response.data.data?.idTransaction}`,
                newTransaction: true,
            }),
        );
        yield take("READ_TRANSACTION_REQUEST");
        yield put({ type: types.ECHEQ_RULE_SEND_SUCCESS });
    }
}

function* downloadErrorsEcheqRulesList({ format, numberRule }) {
    const { type, data } = yield call(downloadErrorsEcheqRules, format, numberRule);

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_ECHEQ_RULE_ERRORS_FAILURE });
        yield put(
            notificationActions.showNotification(
                i18n.get("global.unexpectedError"),
                "error",
                ["echeqRules"],
                null,
                false,
            ),
        );
    } else {
        const { content, fileName } = data.data;

        downloadXls(fileName, content);

        yield put({ type: types.DOWNLOAD_ECHEQ_RULE_ERRORS_SUCCESS });
        yield put(
            notificationActions.showNotification(
                i18n.get("global.download.success"),
                "success",
                ["echeqRules"],
                null,
                false,
            ),
        );
    }
}
